/**
 * Generic Category Tours Page
 * Handles any category dynamically
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/Common/SEO';
import {
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  StarIcon,
  CameraIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { tourService } from '../services/tourService';
import { categoryService } from '../services/categoryService';
import { useTheme } from '../contexts/ThemeContext';
import TourCard from '../components/Tours/TourCard';
import PricingFilter from '../components/Common/PricingFilter';
import { TourCardSkeleton } from '../components/Common/Skeleton';

const CategoryTours: React.FC = () => {
  const { categoryName } = useParams<{ categoryName: string }>();
  const { actualTheme } = useTheme();
  const [tours, setTours] = useState<any[]>([]);
  const [category, setCategory] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (categoryName) {
      loadCategoryData();
    }
  }, [categoryName]);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load category info
      const allCategories = await categoryService.getAllCategories();
      const categoryInfo = allCategories.find(cat =>
        cat.name.toLowerCase() === categoryName?.toLowerCase()
      );

      if (categoryInfo) {
        setCategory(categoryInfo);
      }

      // Load tours for this category
      const categoryTours = await tourService.getToursByCategory(categoryName!);
      setTours(categoryTours);
    } catch (error) {
      console.error('Error loading category data:', error);
      setError('Failed to load tours for this category');
    } finally {
      setLoading(false);
    }
  };


  const getCategoryColor = (categoryName: string) => {
    if (category?.color) return category.color;

    // Fallback colors for known categories
    const colorMap: { [key: string]: string } = {
      'safari': '#059669',
      'cultural': '#DC2626',
      'adventure': '#7C3AED',
      'photography': '#EA580C',
      'wildlife': '#0891B2'
    };

    return colorMap[categoryName?.toLowerCase() || ''] || '#6B7280';
  };

  if (loading && tours.length === 0) {
    return (
      <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <TourCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className={`text-6xl mb-4 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-500' : 'text-gray-400'
              }`}>
              ⚠️
            </div>
            <h1 className={`text-2xl font-bold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
              }`}>
              Error Loading Tours
            </h1>
            <p className={`text-lg transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
              }`}>
              {error}
            </p>
            <Link
              to="/tours"
              className={`inline-flex items-center mt-4 px-4 py-2 rounded-modern transition-all duration-300 hover-magnify-sm modern-focus ${actualTheme === 'dark'
                ? 'bg-primary-600 hover:bg-primary-700 text-white'
                : 'bg-primary-500 hover:bg-primary-600 text-white'
                }`}
            >
              View All Tours
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
      }`}>
      <SEO
        title={`${categoryName ? categoryName.charAt(0).toUpperCase() + categoryName.slice(1) : 'Category'} Tours`}
        description={category?.description || `Authentic ${categoryName} safari experiences with Maasai guides.`}
        image={tours?.[0]?.images?.[0]}
        keywords={[categoryName || '', 'safari', 'adventure', 'kenya']}
      />
      {/* Header */}
      <div className={`py-12 sm:py-20 transition-all duration-300 ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex flex-col items-center justify-center mb-6">
              <div className="mb-4">
                <span
                  className="inline-flex items-center px-6 py-3 rounded-full text-base font-bold text-white shadow-2xl backdrop-blur-lg border-2 border-white/40"
                  style={{
                    backgroundColor: `${getCategoryColor(categoryName || '')}DD`,
                    boxShadow: `0 10px 25px ${getCategoryColor(categoryName || '')}60, 0 0 20px ${getCategoryColor(categoryName || '')}40, inset 0 1px 0 rgba(255,255,255,0.2)`
                  }}
                >
                  {categoryName ? categoryName.charAt(0).toUpperCase() + categoryName.slice(1) : 'Category'}
                </span>
              </div>
              <div>
                <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
                  }`}>
                  {categoryName ? categoryName.charAt(0).toUpperCase() + categoryName.slice(1) : 'Category'} Tours
                </h1>
                {category?.description && (
                  <p className={`text-lg transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                    }`}>
                    {category.description}
                  </p>
                )}
              </div>
            </div>

            <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300 ${actualTheme === 'dark'
              ? 'bg-slate-700 text-slate-300'
              : 'bg-gray-100 text-gray-700'
              }`}>
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              {tours.length} {tours.length === 1 ? 'Tour' : 'Tours'} Available
            </div>
          </div>
        </div>
      </div>

      {/* Tours Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {tours.length === 0 ? (
          <div className="text-center py-12">
            <div className="mb-4">
              <span
                className="inline-flex items-center px-6 py-3 rounded-full text-base font-bold text-white shadow-2xl backdrop-blur-lg border-2 border-white/40"
                style={{
                  backgroundColor: `${getCategoryColor(categoryName || '')}DD`,
                  boxShadow: `0 10px 25px ${getCategoryColor(categoryName || '')}60, 0 0 20px ${getCategoryColor(categoryName || '')}40, inset 0 1px 0 rgba(255,255,255,0.2)`
                }}
              >
                {categoryName ? categoryName.charAt(0).toUpperCase() + categoryName.slice(1) : 'Category'}
              </span>
            </div>
            <h2 className={`text-2xl font-bold mb-2 transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
              }`}>
              No Tours Found
            </h2>
            <p className={`text-lg transition-colors duration-300 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
              }`}>
              We don't have any {categoryName?.toLowerCase()} tours available at the moment.
            </p>
            <Link
              to="/tours"
              className={`inline-flex items-center mt-4 px-4 py-2 rounded-modern transition-all duration-300 hover-magnify-sm modern-focus ${actualTheme === 'dark'
                ? 'bg-primary-600 hover:bg-primary-700 text-white'
                : 'bg-primary-500 hover:bg-primary-600 text-white'
                }`}
            >
              View All Tours
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {tours.map((tour) => (
              <TourCard
                key={tour.id}
                tour={tour}
                showQuickBook={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryTours;
